import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { conns } from '@cfg/axiosConfig.tsx';
import type { RootState } from '@store/store';
import { SaleOrder, SaleOrderCreate } from '@eshop/types/order.types.ts';

interface SaleOrderPage {
  count: number;
  page: number;
  page_size: number;
  results: SaleOrder[];
}

interface SaleOrdersState {
  orders: SaleOrder[];
  count: number;
  page: number;
  pageSize: number;
  status: 'idle' | 'loading' | 'failed';
  error: string | null;
}

const initialState: SaleOrdersState = {
  orders: [],
  count: 0,
  page: 1,
  pageSize: 10,
  status: 'idle',
  error: null,
};

export const createSaleOrder = createAsyncThunk<
  SaleOrder,
  SaleOrderCreate,
  { rejectValue: string }
>(
  'saleOrders/create',
  async (payload: SaleOrderCreate, { rejectWithValue }) => {
    try {
      const { data } = await conns<SaleOrder>('eshop:checkout:validate').post(payload);
      return data;
    } catch (err) {
      return rejectWithValue(`Failed to create order: ${err}`);
    }
  }
);

export const fetchSaleOrder = createAsyncThunk(
  'saleOrders/get',
  async (id: number, { rejectWithValue }) => {
    try {
      const { data } = await conns<SaleOrder>('eshop:order:create', [id]).get();
      return data;
    } catch (err) {
      return rejectWithValue(`Failed to fetch order: ${err}`);
    }
  }
);

export const listSaleOrders = createAsyncThunk<
  SaleOrderPage,
  { page?: number; pageSize?: number } | undefined,
  { rejectValue: string; state: RootState }
>(
  'saleOrders/list',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const page = params?.page ?? 1;
      const pageSize = params?.pageSize ?? 10;
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated ?? false;
      const guestToken = state.user?.guestToken ?? null;
      const requestConfig =
        !isAuthenticated && guestToken
          ? { headers: { 'X-Guest-Token': guestToken } }
          : {};
      const { data } = await conns<SaleOrderPage>('eshop:order:list').get(
        {
          page,
          page_size: pageSize,
        },
        requestConfig,
      );
      return data;
    } catch (err) {
      return rejectWithValue(`Failed to list orders: ${err}`);
    }
  }
);

const saleOrdersSlice = createSlice({
  name: 'saleOrders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createSaleOrder.fulfilled, (state, action) => {
        state.orders = [action.payload, ...state.orders];
        state.count += 1;
        state.status = 'idle';
        state.error = null;
      })
      .addCase(createSaleOrder.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createSaleOrder.rejected, (state, action) => {
        state.status = 'failed';
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to create order';
      })
      .addCase(listSaleOrders.fulfilled, (state, action) => {
        state.orders = action.payload.results;
        state.count = action.payload.count;
        state.page = action.payload.page;
        state.pageSize = action.payload.page_size;
        state.status = 'idle';
        state.error = null;
      })
      .addCase(listSaleOrders.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(listSaleOrders.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Failed to list orders';
      });
  },
});

export const selectSaleOrders = (s: RootState) => s?.saleOrders?.orders ?? [];
export const selectSaleOrdersStatus = (s: RootState) => s?.saleOrders?.status ?? 'idle';
export const selectSaleOrdersCount = (s: RootState) => s?.saleOrders?.count ?? 0;
export const selectSaleOrdersPage = (s: RootState) => s?.saleOrders?.page ?? 1;
export const selectSaleOrdersPageSize = (s: RootState) => s?.saleOrders?.pageSize ?? 10;
export const selectSaleOrdersError = (s: RootState) => s?.saleOrders?.error;

export default saleOrdersSlice.reducer;
